import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaBook, FaPlus, FaLock, FaChevronDown, FaGraduationCap, FaExpand } from 'react-icons/fa';
import { fetchUserLists } from '../../utils/listsApi';
import { useAuth } from '../../contexts/AuthContext';
import CreateListModal from './CreateListModal';
import EditSmartListRuleModal from './EditSmartListRuleModal';

const STORAGE_KEY = 'codebattles:listsExpanded';

export default function MyListsSidebar({ currentListId }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [userLists, setUserLists] = useState([]);
  const [isListsExpanded, setIsListsExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'true'); } catch { return true; }
  });
  const [showListsDropdown, setShowListsDropdown] = useState(false);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [showSmartListModal, setShowSmartListModal] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setUserLists([]);
      return;
    }
    let cancelled = false;
    fetchUserLists()
      .then((lists) => { if (!cancelled) setUserLists(lists || []); })
      .catch(() => { if (!cancelled) setUserLists([]); });
    return () => { cancelled = true; };
  }, [currentUser]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(isListsExpanded)); } catch (e) {}
  }, [isListsExpanded]);

  const handleCreated = (list) => {
    setShowCreateListModal(false);
    if (list?.id) {
      setUserLists((prev) => [...prev, { id: list.id, name: list.name, icon: list.icon || 'book', isDefault: false, isPrivate: list.isPrivate, problemCount: 0, progress: { solved: 0, attempting: 0, todo: 0 } }]);
      navigate(`/lists/${list.id}`);
    }
  };

  return (
    <div className="w-full lg:w-64 space-y-6 flex-shrink-0">
      {/* Library section - LeetCode style */}
      <div className="card p-4 space-y-3">
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Library</h2>
        <ul className="space-y-1">
          <li>
            <button
              onClick={() => navigate('/problems')}
              className="w-full text-left flex items-center gap-2 p-2 rounded-md hover:bg-background-tertiary transition-colors text-sm text-text-primary"
            >
              <FaBook className="flex-shrink-0 text-text-secondary" size={14} /> Problems
            </button>
          </li>
          <li>
            <button
              className="w-full text-left flex items-center gap-2 p-2 rounded-md hover:bg-background-tertiary transition-colors text-sm text-text-tertiary cursor-not-allowed"
              disabled
            >
              <FaGraduationCap className="flex-shrink-0" size={14} /> Study Plan
            </button>
          </li>
        </ul>
      </div>

      {/* My Lists */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setIsListsExpanded(!isListsExpanded)}
            className="text-lg font-bold flex items-center gap-2 hover:opacity-90 text-text-primary w-full text-left"
          >
            <FaChevronDown className={`transition-transform flex-shrink-0 ${isListsExpanded ? '' : '-rotate-90'}`} />
            My Lists
          </button>
          {currentUser && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowListsDropdown(!showListsDropdown)}
                className="p-1.5 rounded-md hover:bg-background-tertiary flex items-center gap-0.5 text-text-secondary"
                title="Add or create list"
              >
                <FaPlus size={14} />
                <FaChevronDown size={10} className={showListsDropdown ? 'rotate-180' : ''} />
              </button>
              {showListsDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowListsDropdown(false)} />
                  <div className="absolute right-0 top-full mt-1 py-1 w-48 bg-background-card border border-border-primary rounded-lg shadow-lg z-20">
                    <button
                      onClick={() => { setShowListsDropdown(false); setShowCreateListModal(true); }}
                      className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-background-secondary flex items-center gap-2 rounded-t-lg"
                    >
                      <FaPlus size={12} /> New List
                    </button>
                    <button
                      onClick={() => { setShowListsDropdown(false); setShowSmartListModal(true); }}
                      className="w-full text-left px-3 py-2 text-sm text-purple-500 hover:bg-background-secondary flex items-center gap-2 rounded-b-lg"
                    >
                      New Smart List...
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {isListsExpanded && (
          <>
            {!currentUser ? (
              <p className="text-sm text-text-tertiary">Sign in to create and view lists</p>
            ) : userLists.length === 0 ? (
              <p className="text-sm text-text-tertiary">No lists yet. Create one with +</p>
            ) : (
              <ul className="space-y-1">
                {userLists.map((list) => {
                  const isStar = list.icon === 'star' || list.isDefault;
                  const isActive = currentListId && String(list.id) === String(currentListId);
                  return (
                    <li key={list.id}>
                      <button
                        onClick={() => navigate(`/lists/${list.id}`)}
                        className={`w-full text-left flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors text-sm border ${
                          isActive
                            ? 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 ring-1 ring-border-primary'
                            : 'bg-gray-50 dark:bg-gray-800/50 border-transparent hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700'
                        }`}
                      >
                        {isStar ? (
                          <FaStar className="text-yellow-500 flex-shrink-0" size={14} />
                        ) : (
                          <FaBook className="text-gray-600 dark:text-gray-400 flex-shrink-0" size={14} />
                        )}
                        <span className="truncate flex-1 min-w-0 text-text-primary font-medium">{list.name}</span>
                        <span className="text-text-tertiary text-xs flex-shrink-0">({list.problemCount ?? 0})</span>
                        <FaExpand size={10} className="text-text-tertiary flex-shrink-0 opacity-70" title="Expand Panel" />
                        {list.isPrivate && <FaLock className="text-text-tertiary flex-shrink-0" size={10} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {showCreateListModal && (
        <CreateListModal
          onClose={() => setShowCreateListModal(false)}
          onCreated={handleCreated}
        />
      )}
      {showSmartListModal && (
        <EditSmartListRuleModal
          onClose={() => setShowSmartListModal(false)}
          onCreated={(list) => {
            setShowSmartListModal(false);
            if (list?.id) {
              setUserLists(prev => [...(prev || []), { id: list.id, name: list.name, icon: 'book', isDefault: false, isPrivate: true, problemCount: 0, progress: { solved: 0, attempting: 0, todo: 0 } }]);
              navigate(`/lists/${list.id}`);
            }
          }}
        />
      )}
    </div>
  );
}
